// テキスト配置前の「文字の色」「背景の色」「太字」の選好を localStorage に保存する。
// 文字サイズ（text-size-preference）・文字寄せ（text-align-preference）と役割は同じだが、
// 保存の成否を戻り値で返す点だけが異なる。呼び出し側（canvas-store の setter）は画面を先に
// 更新する楽観更新とし、保存できなかったときだけ直前値へ戻して利用者へ通知できるようにする。

import type { TextAlign } from "@refinement-canvas/domain";
import { isValidHexColor, normalizeHexColor } from "./hex-color";
import { TEXT_DEFAULT_COLOR, TEXT_FILL_TRANSPARENT } from "./text-color";

const COLOR_KEY = "refinement-canvas:text:color";
const FILL_KEY = "refinement-canvas:text:fill";
const BOLD_KEY = "refinement-canvas:text:bold";

/** 保存値が無いとき・壊れているときに使う既定の太字。 */
export const DEFAULT_TEXT_BOLD = false;

/**
 * 文字色として受理できる値だけを 6 桁 hex（小文字）へ正規化する。
 * 受理できない値では null を返し、呼び出し側が state も保存値も変えないようにする。
 */
export function normalizeTextColorPreference(color: unknown): string | null {
  return isValidHexColor(color) ? normalizeHexColor(color) : null;
}

/**
 * 背景色として受理できるのは 6 桁 hex と「背景なし」（"transparent"）だけ。
 * CSS へ不正な色が流れないよう、ここで弾いた値は保存も適用もしない。
 */
export function normalizeTextFillPreference(fill: unknown): string | null {
  if (fill === TEXT_FILL_TRANSPARENT) return TEXT_FILL_TRANSPARENT;
  return isValidHexColor(fill) ? normalizeHexColor(fill) : null;
}

/** localStorage へ 1 件書き込み、書き込めたかどうかを返す。 */
function writePreference(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    // プライベートブラウジング・容量超過など、保存できない環境では false を返して
    // 呼び出し側が楽観更新をロールバックできるようにする。
    return false;
  }
}

/** 配置前の文字色を読み込む。保存値が無い・壊れている場合は既定文字色へ戻す。 */
export function loadTextColor(): string {
  try {
    return (
      normalizeTextColorPreference(window.localStorage.getItem(COLOR_KEY)) ?? TEXT_DEFAULT_COLOR
    );
  } catch {
    return TEXT_DEFAULT_COLOR;
  }
}

export function saveTextColor(color: string): boolean {
  const normalized = normalizeTextColorPreference(color);
  if (normalized === null) return false;
  return writePreference(COLOR_KEY, normalized);
}

/** 配置前の背景色を読み込む。保存値が無い・壊れている場合は背景なしへ戻す。 */
export function loadTextFill(): string {
  try {
    return normalizeTextFillPreference(window.localStorage.getItem(FILL_KEY)) ?? TEXT_FILL_TRANSPARENT;
  } catch {
    return TEXT_FILL_TRANSPARENT;
  }
}

export function saveTextFill(fill: string): boolean {
  const normalized = normalizeTextFillPreference(fill);
  if (normalized === null) return false;
  return writePreference(FILL_KEY, normalized);
}

/** 配置前の太字を読み込む。"on" 以外（未保存・壊れた値）はすべて既定の false へ戻す。 */
export function loadTextBold(): boolean {
  try {
    return window.localStorage.getItem(BOLD_KEY) === "on";
  } catch {
    return DEFAULT_TEXT_BOLD;
  }
}

export function saveTextBold(bold: boolean): boolean {
  return writePreference(BOLD_KEY, bold ? "on" : "off");
}

/** 新規テキストへ持たせる初期書式。配置前パネルの 5 項目をすべて明示的に渡す。 */
export interface TextCreateStyle {
  readonly color: string;
  readonly fill: string;
  readonly fontSize: number;
  readonly bold: boolean;
  readonly textAlign: TextAlign;
}

/**
 * 配置前の選好から、新規テキストの初期書式を組み立てる。
 *
 * 「背景なし」は既存オブジェクト契約どおり "transparent" のまま渡す（op スキーマは
 * 「未設定」を表現できないため、透明色を明示して背景なし表示にする）。
 * `fontSize` と `textAlign` は現行の配置と同じ値をそのまま通し、初期高さの算出規則も変えない。
 */
export function resolveTextCreateStyle(input: TextCreateStyle): TextCreateStyle {
  return {
    color: normalizeTextColorPreference(input.color) ?? TEXT_DEFAULT_COLOR,
    fill: normalizeTextFillPreference(input.fill) ?? TEXT_FILL_TRANSPARENT,
    fontSize: input.fontSize,
    bold: input.bold,
    textAlign: input.textAlign,
  };
}
